import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldOff, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Soft landing surfaced when the customer-side auth flow detects that
 * the user's account has been deactivated by an admin. Reached from:
 *   - AuthPage login error handler (403 + "deactivated" in detail)
 *   - customerAuthInterceptor on /auth/refresh 403
 *
 * Friendlier than a toast — explains the situation, points at support,
 * and offers a way back to /auth in case the user wants to sign in with
 * a different account.
 */
export default function AccountDeactivated() {
  const [supportEmail, setSupportEmail] = useState('support@smashzone.asia');

  useEffect(() => {
    // Resolve the support email from the public-settings endpoint so the
    // mailto: target matches whatever is configured in admin Settings.
    // Best-effort — fall back to the hardcoded default on any error so
    // the page always renders something tappable.
    let cancelled = false;
    axios
      .get(`${API}/settings/public`)
      .then((res) => {
        if (!cancelled && res.data?.support_email) {
          setSupportEmail(res.data.support_email);
        }
      })
      .catch(() => { /* keep hardcoded default */ });
    return () => { cancelled = true; };
  }, []);

  const mailto = `mailto:${supportEmail}?subject=${encodeURIComponent('Help re-activating my SmashZone account')}`;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50">
      <Card className="max-w-md w-full p-8" data-testid="account-deactivated-page">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center">
            <ShieldOff className="w-8 h-8 text-rose-600" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-center text-slate-900 mb-3">
          Your account is inactive
        </h1>

        <p className="text-slate-600 text-center mb-6 leading-relaxed">
          You can't sign in or make bookings while your account is in this state.
          Our support team can help you sort it out.
        </p>

        <Button
          asChild
          className="w-full bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="contact-support-btn"
        >
          <a href={mailto}>
            <Mail className="w-4 h-4 mr-2" />
            Contact Support
          </a>
        </Button>

        <div className="mt-4 text-center">
          <Link
            to="/auth"
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
            data-testid="back-to-signin-link"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1" />
            Sign in with a different account
          </Link>
        </div>

        <p className="mt-6 text-xs text-slate-400 text-center">
          Support email: <span className="font-mono">{supportEmail}</span>
        </p>
      </Card>
    </div>
  );
}
